/**
 * Flying into a building — AD-020.
 *
 * **A declared enhancement, and the module note says so first.** The original
 * does not do this. `collide.c`'s collision check switches on the object kind
 * at `0x1B0E0` and handles 0, 1 and 2 — plane, Zeppelin, balloon — falling
 * through to `continue` for everything else (`collisions-design.md` §3.1). A
 * placed cottage is not any of those; it is not an object at all. `.WLD`
 * scenery is terrain furniture drawn from `.GI` footprints, outside the
 * per-object check entirely, and the only terrain interaction anywhere in the
 * recovered record is the take-off and landing ground handling. So in Red Baron
 * (1990) you fly through the church, and this port did too until this file.
 *
 * The user asked for it anyway (roadmap B4, 2026-08-26, verbatim: *"crashing
 * into a bulding on the ground does not register properly, you don't seem to
 * crash just fly through it"*), and it was DECIDED as a wanted enhancement on
 * the same day. `scenery-fires.ts` and `crash-sites.ts` are already extensions
 * of the same kind and state it the same way.
 *
 * ## The hull: AD-018's footprint, and AD-017's radius
 *
 * Nothing new is measured here. The building is the box AD-018 already
 * hit-tests bullets against — the `.GI` half-extent pair extruded to the
 * object's tallest facet, **unrotated**, because the original's own
 * `giTestObjectAt` never applies the placement's `.WLD` heading either
 * (gi-format.md §5). The aeroplane is the sphere AD-017 already rams other
 * aeroplanes with, `airframeCollisionRadiusM` off `collisionSize` in the plane
 * table: 3.81 m for a scout, 6.86 m for a two-seater. A pilot therefore hits a
 * barn with exactly the hull he hits an Albatros with, and a wall with exactly
 * the box his rounds go into. Two features, no third geometry, and no number
 * invented for this one.
 *
 * The two meet through the same `SceneryQuery` seam AD-018 declared, with one
 * optional argument added: `padM`, the radius to grow the box by. That is the
 * Minkowski sum of the box and the sphere, approximated by the box's own
 * enlargement, which is the standard conservative reading and errs by at most a
 * corner. Bullets pass no `padM` and get the identical answer they got before
 * this lane, which is what keeps AD-018 unmoved.
 *
 * ## The ground frame is not this file's problem, and that is the point
 *
 * `SceneryStructure.baseY` is filled in by the app's `regionStructures`, which
 * is handed `ReliefGround.drapeAt` — the **drawn** surface, per `6471386`'s
 * lesson and the church-at-+34-m bug `TERR5-33` is the canonical example of.
 * Reusing AD-018's structures rather than deriving a second set is what makes
 * that inheritance automatic: there is no second answer here for where the
 * ground is, because there is no second box.
 *
 * ## Player only
 *
 * `entry.ai === null`, and this is consistency rather than a shortcut. The
 * original's collision check tests `ds:0x431e` — the player's own object
 * pointer — and skips any pair with no human in it (AD-017 §3, finding 1):
 * *"the original models ramming as something that happens to the player, never
 * as an event in the wider simulated sky."* `ramming.ts` already ports that
 * scoping verbatim. Making scenery solid for the AI as well would be a wider
 * claim than the original makes about any collision, and it would be a bad one:
 * nothing in `ai-pilot.ts` knows a building exists, so a flight sent down on a
 * ground-attack route would fly into the village it was sent to attack, and
 * every census in the corpus would move for a reason nobody asked for.
 *
 * ## Realism: AIRCRAFT MAY BE DAMAGED, and deliberately not MIDAIR COLLISIONS
 *
 * `collide.c` tests two flags above its kind switch, in order: AIRCRAFT MAY BE
 * DAMAGED at `0x1B084` and MIDAIR COLLISIONS at `0x1B08E`. This takes the first
 * and not the second, for three reasons.
 *
 * - MIDAIR COLLISIONS is a recovered flag with a recovered meaning: it gates
 *   the *object* check, whose whole subject is the three flying kinds. A barn
 *   is not in that switch at all, so borrowing the toggle would be attaching a
 *   pinned word to a question it was never read for.
 * - AIRCRAFT MAY BE DAMAGED is the port's existing master mercy on the player's
 *   own seat — `applyHits`'s `damageable`, `flak.ts`'s `isPlayer` scaling, and
 *   `ram-pass.ts`'s first test are all the same flag. A pilot who has asked not
 *   to be hurt is not hurt by the hangar either, which is the same bargain he
 *   already has with every bullet in the sky.
 * - It is the one that ships **on**. `MISSION_REALISM` — what every generated
 *   mission stamps and what `DEFAULT_REALISM` copies — carries
 *   `aircraftMayBeDamaged: true` and `midairCollisions: false`. Gating on the
 *   second would have delivered a feature that is invisible out of the box,
 *   which is precisely the complaint that asked for it.
 *
 * NOVICE (`ALL_REALISM_OFF`) therefore still flies through the church. He is
 * also still killed by the ground, because `applyGround` reads no realism flag;
 * that asymmetry is real and is chosen with it open — the ground is the world's
 * floor and cannot be opted out of, whereas this is an enhancement, and an
 * enhancement that kills a novice who did not ask for it is worse than one he
 * never notices.
 *
 * ## What a strike does
 *
 * The same thing flying into a hillside does: `sim.crashed`. Not the ram
 * treatment — a ram takes both wing cells off and lets AD-014's death arc fly
 * the wreck down, which is right for two aeroplanes that glance and wrong for
 * an airframe that hit a stone wall at forty metres a second and stopped. So
 * the velocity goes to zero, the rates go to zero, the undercarriage is folded
 * and `crashed` is raised, which is byte-for-byte what `ground-contact.ts`
 * produces for a bad arrival. Everything downstream is then free and already
 * tested: `step` returns `restingTick` for ever after, `aircraftStatus` reads
 * `crashed` as `destroyed`, `arrivedOnGround` is true, and `settle` files a
 * `CrashSite` at the foot of the building with the terrain height under it.
 *
 * The airframe comes to rest *where it stopped*, which for a tall structure is
 * off the ground — an aeroplane in a church tower is in the church tower. The
 * burning mark below it is at ground level because `crash-sites.ts` samples the
 * `GroundQuery` for its own `y`, and that is the honest picture of both.
 *
 * ## Cost
 *
 * One `structureOnSegment` call per tick, for one aeroplane, and only when his
 * panel has AIRCRAFT MAY BE DAMAGED on. Above the region's tallest roof plus
 * his own radius that call is two float compares and a `null` — the height
 * reject AD-018 built, which is why a sortie at patrol height never touches the
 * grid. AD-018's refusal to put 162–235 structures into the per-tick object
 * loop is inherited whole: this pass never sees a structure list, only a query.
 */

import { ZERO_RATES, ZERO_VEC3 } from "../flight/math3d.ts";
import type { SimState } from "../flight/state.ts";
import type { CombatAircraft } from "./encounter.ts";
import { airframeCollisionRadiusM } from "./ramming.ts";
import { igniteScenery, type SceneryFire, type SceneryQuery, type SceneryStructure } from "./scenery-fires.ts";

/** One aeroplane's pre-step record paired with the state its own `step` produced. */
export interface BuildingStep {
  readonly entry: CombatAircraft;
  readonly sim: SimState;
}

/** One aeroplane that ended a tick inside a building it started the tick outside of. */
export interface BuildingStrike {
  readonly aircraftId: string;
  /** The structure it went into — AD-018's record, so `igniteScenery` can light it. */
  readonly structure: SceneryStructure;
}

export interface BuildingPassResult {
  /**
   * The list handed in, with every struck aeroplane's `sim` replaced by the
   * stopped one. **The same array reference** when nothing was struck, which is
   * every tick of every sortie but one.
   */
  readonly flown: readonly BuildingStep[];
  readonly strikes: readonly BuildingStrike[];
  /**
   * The fire list handed in, with every struck structure lit — the same array
   * reference when nothing was struck. It rides on this result rather than
   * being folded in by the caller because a strike and its fire are one event,
   * and `encounter-tick.ts`'s job is to order the passes, not to spell them.
   */
  readonly fires: readonly SceneryFire[];
}

const NO_STRIKES: readonly BuildingStrike[] = [];

/**
 * Whether this aeroplane can hit a building at all, before any geometry is
 * looked at. Three cheap rejects, in the order that costs least:
 *
 * - not the player (see the module note on `ds:0x431e`),
 * - already finished — a wreck that has arrived has left the world's geometry,
 *   exactly as `ram-pass.ts`'s `present` says, and one already `crashed` is
 *   resting and must not be crashed a second time,
 * - AIRCRAFT MAY BE DAMAGED off.
 */
function solidFor(stepped: BuildingStep): boolean {
  if (stepped.entry.ai !== null) return false;
  if (stepped.entry.status === "destroyed") return false;
  if (stepped.sim.crashed) return false;
  return stepped.sim.config.realism.aircraftMayBeDamaged;
}

/**
 * The airframe stopped in the wall — `ground-contact.ts`'s crash verdict, with
 * the wall standing in for the surface.
 *
 * The position is left exactly where the step put it rather than pulled back to
 * the face it entered. At 60 Hz and a hundred metres a second that is 1.7 m of
 * overlap at the very worst, and an aeroplane a metre inside the wall it hit is
 * a better picture than one hovering a millimetre off it.
 *
 * `readout` is deliberately untouched. `ground-contact.ts` does the same on a
 * ground crash — `readoutOf` takes its airspeed from the *motion*, not from the
 * arrested velocity — so the final instrument reading is the one the pilot had
 * when he hit, in both cases.
 */
export function stopAtStructure(sim: SimState): SimState {
  return {
    ...sim,
    velocity: ZERO_VEC3,
    rates: ZERO_RATES,
    gear: { onGround: true, collapsed: true, stopped: true },
    crashed: true,
    events: sim.events.includes("crashed") ? sim.events : [...sim.events, "crashed"],
  };
}

/**
 * Pass 3c, and the ignition that goes with it. Runs on the **stepped** poses,
 * like the ram pass and for the same
 * reason `intel\move.c` gives: nobody aims a collision, so an object is moved
 * and then asked whether it ended up inside something.
 *
 * The segment is the tick's own displacement — pre-step position to post-step
 * position — so the aeroplane is tested over exactly the path it flew and a
 * dive that would clear a roof between two samples cannot tunnel through it.
 *
 * **A segment that starts inside the volume is not a strike**, which is where
 * this parts company with `segmentBoxEntry`'s bullet convention. A round is
 * created fresh each time it is swept, so "already in the wall" means it just
 * arrived; an aeroplane persists across ticks, so it means it was *put* there —
 * spawned on an aerodrome with a hangar inside its own collision radius, or
 * teleported by a mission setup. Killing a pilot for where he was placed is a
 * bug with no upside, so the entry is confirmed with a second, degenerate query
 * at the start point (a zero-length segment is a point-in-box test in
 * `segmentBoxEntry`'s slab arithmetic) and only paid for on the rare tick a
 * strike was detected at all.
 *
 * `fires` is `sceneryPass`'s already-aged list, so a barn lit here gets a full
 * `burnSeconds` rather than one tick less — `strikeScenery`'s own rule.
 */
export function buildingPass(
  flown: readonly BuildingStep[],
  scenery: SceneryQuery,
  fires: readonly SceneryFire[] = [],
): BuildingPassResult {
  let strikes: BuildingStrike[] | null = null;
  let struck: BuildingStep[] | null = null;
  let lit = fires;
  for (let index = 0; index < flown.length; index += 1) {
    const stepped = flown[index];
    if (!stepped) continue;
    if (!solidFor(stepped)) continue;
    const padM = airframeCollisionRadiusM(stepped.sim.config.spec.structure.collisionSizeRaw);
    const from = stepped.entry.sim.position;
    const structure = scenery.structureOnSegment(from, stepped.sim.position, padM);
    if (structure === null) continue;
    if (scenery.structureOnSegment(from, from, padM) !== null) continue;
    struck ??= [...flown];
    strikes ??= [];
    struck[index] = { entry: stepped.entry, sim: stopAtStructure(stepped.sim) };
    strikes.push({ aircraftId: stepped.entry.id, structure });
    lit = igniteScenery(lit, structure);
  }
  if (struck === null || strikes === null) return { flown, strikes: NO_STRIKES, fires };
  return { flown: struck, strikes, fires: lit };
}
